import { IntelligenceFixture } from '../../types';
import { PLACEHOLDER_WHY_THIS_RESULT } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

/* Every FAS term raises a 0-100 SEVERITY score, per its own `governed_formula_expression`, so each
   is a blocker. The direction is the registry's factor set and not `riseMeans`, which transcribes
   the `#EE6475` the file paints on this tile's falling delta chip. PLA-8815. */
const BLOCKS = 'blocker' as const;

export const riskProtectionCardFixtures: Record<string, IntelligenceFixture> = {
  risk_unusual_activity: {
    cardKey: 'risk_unusual_activity',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // Three KPI tiles, in the screen's order. `flagged_customers` 1,240 is NOT renamed into
    // `high_risk_customers`: 5,184 is a different population, so the old reading is dropped rather
    // than carried across under a new key.
    metrics: [
      {
        key: 'fraud_anomaly_score',
        // O-COM-022's own driver means, UAT `generic` 2026-09-08, strongest first. Four of the
        // formula's six terms: `velocity_zscore` and `amount_outlier` answered `null` and are
        // dropped rather than drawn at zero. The other three ARE a calculated zero, which is a
        // tenant with almost no fraud signal — its FAS reads 3.32. Every term raises a severity
        // score, so all four are blockers. PLA-8815.
        factors: [
          { key: 'fas_mean_factor_device_ip', label: 'Device/IP risk', value: 0.07, tone: BLOCKS },
          {
            key: 'fas_mean_factor_duplicate_issuance',
            label: 'Duplicate activity',
            value: 0,
            tone: BLOCKS
          },
          {
            key: 'fas_mean_factor_callback_mismatch',
            label: 'Callback mismatch',
            value: 0,
            tone: BLOCKS
          },
          {
            key: 'fas_mean_factor_identity_conflict',
            label: 'Identity conflict',
            value: 0,
            tone: BLOCKS
          }
        ],
        // `589-29273` verbatim, solidus and parenthetical included — the tile is labelled
        // `Fraud / Anomaly Score (FAS)`, not the `Fraud & Anomaly Score` this shipped.
        label: 'Fraud / Anomaly Score (FAS)',
        value: 46,
        format: 'points',
        // `↓ 7 pts`, painted `#EE6475` (design/section-risk-protection-devmode-2026-08-12.md § 3).
        // A falling fraud score reads as a GOOD result, so the intuitive sentiment would be
        // `positive` — but the file paints it in the negative hue, and this band follows the drawn
        // hue rather than the intuition, exactly as Experience & Value's two tiles do. Whether the
        // red encodes the arrow's direction or a judgement is question 7 for the designer.
        delta: { value: 7, unit: 'points', direction: 'down', sentiment: 'negative' }
      },
      {
        key: 'high_risk_customers',
        label: 'High-risk Customers',
        // A DESIGN MOCK, and only that. The live tile reads `high_risk_customer_count` off
        // O-COM-023's own rows and returned 0 on UAT `generic` (2026-09-03) — a calculated zero,
        // not an absence. 5,184 is the screen's own figure and is derived from nothing; it is kept
        // because this fixture is what the design-parity suites render, never because it stands in
        // for a measurement. PLA-8815.
        value: 5184,
        format: 'count',
        // `↓ 2,800 (8.0% pp)`, same hue and same reading as the tile beside it. Left off when the
        // slot was `relativePct` and shipping an irreconcilable reading meant an entry in a by-name
        // exemption list. That blocker is gone — `secondary: { value: 8.0, unit: 'pp' }` now states
        // the form outright — so this is a one-line addition whenever the tile is meant to draw it.
        // Not added here: U1 changed no tile's rendered output.
        delta: { value: 2800, unit: 'count', direction: 'down', sentiment: 'negative' },
        // The tile carries a 142x56 line chart with its peak marked (589-29285). The SHAPE is the
        // file's — a rise to a marked peak about three-quarters along, then a fall — but the file
        // draws a vector, not a series, so these readings are fixture stand-ins for the trend the
        // seam will carry, exactly as `payments.ts` already ships one. They are not read off the
        // file and no reading here is quoted anywhere as the file's own.
        // Nine readings peaking at index 6: that puts the marker at x=103 of 142 and y=7, which is
        // where `top dot` 589-29287 and its drop-line 589-29288 are drawn.
        sparkline: [5020, 5090, 5240, 5610, 5980, 6180, 6620, 5410, 5184]
      },
      {
        key: 'risk_distribution_confidence',
        label: 'Risk-distribution Confidence',
        value: 82,
        format: 'percent',
        // `High`, Inter 700 9px `#1EB980` (589-29293) — a qualifier the reading is measured
        // against, on the value's own line rather than under it.
        status: { label: 'High', tone: 'positive' },
        // Tile 3 draws the same marked line chart as tile 2, in the green of its own `High`. Its
        // widget node is unresolved — three ring walks report the tile as label + value + `High`
        // — but two canvas captures at 93% and 100% draw it; see `TileSparkline`. Stand-in
        // readings, on the same footing as the tile above.
        sparkline: [74, 76, 75, 79, 83, 86, 88, 84, 82]
      },
      /* PLA-8828: the catalogue's O-REV-009, bound now that every catalogue metric with a card gets
         a binding. `null` and not a figure: the endpoint answers 404 `40415` for it on UAT
         (2026-09-18), so no reading has ever been observed, and the file draws no fourth tile to
         transcribe one from. No `sparkline` for the same reason — the two tiles above carry one
         because the file draws them a chart. */
      {
        key: 'suspicious_reversal_pattern_score',
        label: 'Suspicious Reversal Pattern Score',
        value: null,
        format: 'points'
      }
    ],
    // No `evidence` panel. Established by enumerating the whole card top to bottom in the frame
    // render — band, card heading, three tiles, two ring blocks, two whyboxes, EVIDENCE INPUTS,
    // chips, the freshness stamp, and nothing else — not by a probe that came back empty. What sits
    // under the distributions is the `EVIDENCE INPUTS` chip row, which is card visuals rather than
    // payload; see `riskProtectionVisuals.evidenceInputs`.
    // Left as shipped: the card's confidence badge (589-29421) has not been read. The 82% / High on
    // the screen belongs to the third KPI tile, `Risk-distribution Confidence`, which is a different
    // reading from the card-level score and must not be copied onto it.
    confidence: {
      score: 71,
      band: 'medium',
      reasons: ['Device signals available for 74% of the flagged group']
    },
    // The screen's own body is Lorem Ipsum, so the card renders the shared placeholder. The element
    // ships even though the copy has not been written (fixtures/placeholderCopy.ts).
    whyThisResult: PLACEHOLDER_WHY_THIS_RESULT,
    // The card draws a SECOND `div.whybox` beside the first, and the file gives it the same
    // `Why this result?` heading over its own Lorem body. Both are placeholder, so both take the
    // shared stand-in; what the second box is FOR is question 5 for the designer, and drawing it is
    // not conditional on that answer — the file draws it.
    evidenceNote: PLACEHOLDER_WHY_THIS_RESULT
  }
};

// Declared rather than inferred: without it the array literal below widens to a union of "has a
// key" and "has no key", and reading `.key` off the union is a compile error under `strict: false`
// as much as under `strict`.
interface RiskBandRow {
  /** The wire's own `distribution_key`, on the bands O-COM-023 has actually named. */
  key?: string;
  label: string;
}

// The two distributions the screen draws under the metric row, and the evidence inputs under them.
// Read off node 589-29260 (frame 589:27692); every label and figure below is the file's own
// (design/section-risk-protection-devmode-2026-08-12.md § 4.2 and § 5). Raw values only — colour,
// share and formatting stay the section's job, as in every other section here.
export const riskProtectionVisuals = {
  /* The risk bands the right-hand ring divides. Since the ring went live on O-COM-023 this export
     carries LABEL, ORDER and (by position) COLOUR — the design's copy — and nothing else: the
     seven authored counts that closed on 100,000 are gone, because a band ring drawn on them
     would report a population nobody measured. PLA-8815.

     `key` is the wire's `distribution_key`, and only ONE band has one: O-COM-023 answered UAT
     `generic` with a single calculated row, `fas_severity_band` / `low` (100.0%, 1,772 customers),
     on 2026-09-03. The other six are left unkeyed deliberately — the response has never named them,
     and writing a guess here (`medium`, `fraud_anomaly`, …) would be an invented identifier that
     either silently mislabels a real band or matches nothing. `RiskProtectionSection` therefore
     draws every calculated band the response returns, using this label where the key is known and
     the wire's own key where it is not, so an unrecognised band is visibly unlabelled rather than
     invisibly dropped. Adding a key here is the whole change once a response states one. */
  riskSegments: [
    { key: 'low', label: 'Low risk' },
    { label: 'Medium risk' },
    { label: 'High risk' },
    { label: 'Fraud/anomaly' },
    { label: 'Complaint risk' },
    { label: 'Financial stress' },
    { label: 'Do-not-nudge' }
  ] as RiskBandRow[],
  evidenceInputs: [
    'risk_event',
    'fraud_rule_event',
    'transaction_event',
    'reward_event',
    'callback_event',
    'device_event',
    'IP_event',
    'identity_resolution_event',
    'complaint_event',
    'financial_stress_feature',
    'do_not_nudge_event'
  ]
};
